import { CheveronDownIcon, PersonIcon } from '@wkda/icons';
import cx from 'classnames';
import type { MouseEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import NavMenu from '../NavMenu/index.js';
import Paper from '../Paper/index.js';
import UserNavMenu from '../UserNavMenu/index.js';
import VerticalNavMenu, { ItemTitle } from '../VerticalNavMenu/index.js';
import { useMediaQuery } from '../hooks/useMediaQuery.js';
import type {
  HeaderAccountItem,
  NavListItem,
  NavListItemWithDropdowns,
  UserNavList,
  UserSingleNavlistItem,
} from '../shared.types.js';
import { AccountMenu } from './AccountMenu.js';
import MenuIcon from './MenuIcon.js';

export type HeaderProps = {
  navList: Array<NavListItem>;
  LinkComponent?: any;
  logo: {
    src: string;
    alt: string;
  };
  pageUrl?: string;
  MenuText?: ReactNode;
} & (
  | {
      hasUserAccount?: false;
    }
  | {
      hasUserAccount: true;
      /**
       * If given then the user will be considered logged in
       */
      userNavList?: Array<UserNavList>;
      userIsLoggedIn?: boolean;
      loginItem?: HeaderAccountItem;
      loggedInItem?: HeaderAccountItem;
    }
);

export default function Header({
  navList,
  logo,
  LinkComponent = 'a',
  MenuText = 'MENÜ',
  pageUrl,
  ...rest
}: HeaderProps): JSX.Element {
  const navNode = useRef<HTMLUListElement>(null);
  const mobileNavNode = useRef<HTMLButtonElement>(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const [userNavList, logoutItem] = filterAndGetLogoutItem(
    rest.hasUserAccount === true ? rest.userNavList : undefined,
  );

  // Bootstrap breakpoint for lg
  const isLargeScreen = useMediaQuery('(min-width: 992px)');

  useEffect(
    function pageUrlChangeEffect() {
      setIsMobileNavOpen(false);
    },
    [pageUrl],
  );

  useEffect(
    function largeScreenEffect() {
      setIsMobileNavOpen(false);
    },
    [isLargeScreen],
  );

  const accountItem = rest.hasUserAccount
    ? rest.userIsLoggedIn
      ? rest.loggedInItem
      : rest.loginItem
    : undefined;

  return (
    <>
      <header
        className={cx('wa-header', {
          'wa-header--open': isMobileNavOpen,
        })}
      >
        <div className="wa-header__content">
          <LinkComponent
            href="/"
            className={cx('wa-header__logo', {
              'wa-header__logo-hidden': isMobileNavOpen,
            })}
          >
            <img
              className={'img-fluid wa-header__logo-image'}
              src={logo.src}
              alt={logo.alt}
            />
          </LinkComponent>
          <span
            className={cx('wa-header__menu-text', {
              'wa-header__menu-text-hidden': !isMobileNavOpen,
            })}
          >
            {MenuText}
          </span>
          <nav className="wa-header__nav">
            <ul ref={navNode} className="wa-header__list">
              {renderNavListItems(navList)}
            </ul>
          </nav>
          {accountItem ? (
            <div className="wa-header__login-btn-wrapper">
              <LinkComponent
                href={accountItem.link}
                className="wa-header__login-btn"
              >
                {!accountItem.hideIcon && <PersonIcon />}
                <span className="wa-header__login-user-text">
                  {accountItem.title}
                </span>
              </LinkComponent>
            </div>
          ) : null}

          <button
            type="button"
            ref={mobileNavNode}
            className={cx('wa-header__toggle-btn')}
            onClick={toggleMobileNav}
          >
            <MenuIcon isOpen={isMobileNavOpen} text={MenuText} />
          </button>
        </div>
        <Paper
          isElevated
          isFixedTop
          className={cx('wa-header__mobile-nav', {
            'wa-header__mobile-nav--open': isMobileNavOpen,
          })}
        >
          <VerticalNavMenu LinkComponent={LinkComponent} navList={navList} />
          {rest.hasUserAccount ? (
            <>
              {rest.userIsLoggedIn && userNavList ? (
                <UserNavMenu
                  className="wa-nav-item-separator"
                  LinkComponent={LinkComponent}
                  navList={userNavList}
                />
              ) : null}
              <AccountMenu
                isLoggedIn={rest.userIsLoggedIn ?? false}
                accountItem={accountItem}
                logoutItem={logoutItem}
                LinkComponent={LinkComponent}
              />
            </>
          ) : null}
        </Paper>
      </header>

      <div
        className={cx('wa-header__overlay', {
          'wa-header__overlay--open': isMobileNavOpen,
        })}
        onClick={toggleMobileNav}
      />
    </>
  );

  function renderNavListItems(navList: NavListItem[]): JSX.Element[] {
    return navList.map((item, key) => {
      const hasDropDowns = itemHasDropDowns(item);
      return (
        <li
          className={cx('wa-header__list-item', {
            'wa-header__list-item--dropdown': hasDropDowns,
          })}
          key={key}
        >
          {item.link ? (
            <LinkComponent
              className="wa-header__list-item-link"
              href={item.link}
            >
              <ItemTitle {...item} />
            </LinkComponent>
          ) : (
            <button
              className="wa-header__list-item-button"
              onPointerDown={(e) => {
                if (e.pointerType === 'mouse') {
                  e.preventDefault();
                }
              }}
            >
              <ItemTitle {...item} />
              {hasDropDowns ? (
                <CheveronDownIcon className={cx('wa-header__chevron')} />
              ) : null}
            </button>
          )}
          {hasDropDowns ? (
            <NavMenu
              LinkComponent={LinkComponent}
              dropdowns={item.dropDowns}
              className="wa-header__dropdown"
            />
          ) : null}
        </li>
      );
    });
  }

  function toggleMobileNav(_: MouseEvent<HTMLElement>) {
    setIsMobileNavOpen((prevState) => !prevState);
  }

  function itemHasDropDowns(
    item: NavListItem,
  ): item is NavListItemWithDropdowns {
    return item.dropDowns != null && item.dropDowns.length > 0;
  }
}

function filterAndGetLogoutItem(items: UserNavList[] = []) {
  let logoutItem: UserSingleNavlistItem | undefined;
  const filteredItems: Array<UserNavList> = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (
      item !== undefined &&
      'link' in item &&
      item?.link?.includes('logout')
    ) {
      logoutItem = item;
    } else {
      if (item) {
        filteredItems.push(item);
      }
    }
  }

  return [filteredItems, logoutItem] as const;
}
